/**
 * IndexedDB utility for storing and retrieving chat history messages
 * Provides persistent storage for complete chat messages including images
 * Uses the 'idb' library for a cleaner Promise-based API
 */

import { openDB, DBSchema, IDBPDatabase } from "idb";
import { ChatMessageData } from "./types";

interface ChatDB extends DBSchema {
  messages: {
    key: string; // historyId
    value: {
      historyId: string;
      messages: ChatMessageData[];
      lastUpdated: number;
    };
  };
}

const DB_NAME = "imageGenChatHistory";
const DB_VERSION = 1;

let dbPromise: Promise<IDBPDatabase<ChatDB>> | null = null;

/**
 * Gets or creates the IndexedDB instance
 */
const getDB = (): Promise<IDBPDatabase<ChatDB>> => {
  if (!dbPromise) {
    dbPromise = openDB<ChatDB>(DB_NAME, DB_VERSION, {
      upgrade(db) {
        if (!db.objectStoreNames.contains("messages")) {
          db.createObjectStore("messages", { keyPath: "historyId" });
        }
      },
    });
  }
  return dbPromise;
};

/**
 * Saves chat history for a specific historyId
 */
export const saveChatHistory = async (historyId: string, messages: ChatMessageData[]): Promise<void> => {
  const db = await getDB();
  await db.put("messages", {
    historyId,
    messages,
    lastUpdated: Date.now(),
  });
};

/**
 * Retrieves chat history for a specific historyId
 */
export const getChatHistory = async (historyId: string): Promise<ChatMessageData[] | null> => {
  try {
    const db = await getDB();
    const record = await db.get("messages", historyId);
    return record?.messages || null;
  } catch (error) {
    console.error("Error getting chat history from IndexedDB:", error);
    return null;
  }
};

/**
 * Deletes chat history for a specific historyId
 */
export const deleteChatHistory = async (historyId: string): Promise<void> => {
  const db = await getDB();
  await db.delete("messages", historyId);
};

/**
 * Gets all history IDs stored in IndexedDB
 */
export const getAllHistoryIds = async (): Promise<string[]> => {
  const db = await getDB();
  return await db.getAllKeys("messages");
};

/**
 * Clears all chat histories for a specific exercise
 * History IDs are formatted as `${exerciseId}_${fieldId}`
 */
export const clearExerciseHistories = async (exerciseId: string): Promise<void> => {
  const allHistoryIds = await getAllHistoryIds();
  const exerciseHistoryIds = allHistoryIds.filter(id => id.startsWith(`${exerciseId}_`));

  const db = await getDB();
  for (const historyId of exerciseHistoryIds) {
    await db.delete("messages", historyId);
  }
};
